import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import Select from 'react-select';
import CommonService from "../../../services/common.service";
import p2pTransactionService from "../../../services/p2pTransaction.service";
import {
  completeP2pRfqLinkAfterModuleCreate,
  readP2pLaunchContextFromUrl,
} from "../../../utils/p2pRfqLinkAfterCreate";
import {
  clearQuickBuyRequestDraft,
  readQuickBuyRequestDraft,
} from "../../../utils/quickBuyRequestDraft";
import { useLocationMaster } from "../../../hooks/useLocationMaster";
import { useCurrencyMaster } from "../../../hooks/useCurrencyMaster";
import { useProductDetailsMaster } from "../../../hooks/useProductDetailsMaster";
import "../../../assets/styles/buyer_rfq_form.scss";
export default function BuyerRFQForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editRfqId = searchParams.get('edit');
  const isEditMode = !!editRfqId;
  const p2pLaunch = readP2pLaunchContextFromUrl(searchParams);
  const [loadingRFQ, setLoadingRFQ] = useState(false);
  const [p2pPrefillDone, setP2pPrefillDone] = useState(false);
  
  const [form, setForm] = useState({
    productName: "",
    productCategory: "",
    productSubCategory: "",
    productSubSubCategory: "",
    keywords: [],
    quantity: "",
    unit: "",
    currency: "",
    minPrice: "",
    maxPrice: "",
    tradeTerms: "",
    preferences: "",
    description: "",
    hsnCode: "",
    shippingMethod: "",
    destinationPort: "",
    leadTimeDays: "0",
    paymentTerms: "",
    paymentMethod: "",
    shippingCountry: "",
    shippingState: "",
    shippingCity: "",
    shippingAddress: "",
    shippingPincode: "",
    specialRequirements: "",
    buyerLogisticsRequested: false,
  });

  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadedFileUrls, setUploadedFileUrls] = useState([]);
  const [keywordInput, setKeywordInput] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [companyName, setCompanyName] = useState('');
  
  // Track if location data has been loaded for edit mode to prevent infinite loops
  const locationDataLoadedRef = useRef(false);
  
  // Category states
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [subSubCategories, setSubSubCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingSubCategories, setLoadingSubCategories] = useState(false);
  const [loadingSubSubCategories, setLoadingSubSubCategories] = useState(false);
  
  // Master Data Hooks
  const {
    countries: rawCountries,
    states: rawStates,
    cities: rawCities,
    updateListsForCountry,
    updateListsForState,
  } = useLocationMaster();
  const { currencyOptions } = useCurrencyMaster();
  const { attributeUnitOptions, loading: uomLoading } = useProductDetailsMaster();
  
  // Map master data to react-select format
  const countryOptions = rawCountries.map(c => ({ value: c.id, label: c.label }));
  const stateOptions = rawStates.map(s => ({ value: s.id, label: s.label }));
  const cityOptions = rawCities.map(c => ({ value: c.id, label: c.label }));
  
  // Helper function to get currency label from value (ID or code)
  const getCurrencyLabel = (currencyValue) => {
    if (!currencyValue) return null;
    
    // First try to find by value (ID)
    const foundByValue = currencyOptions.find(opt => 
      String(opt.value) === String(currencyValue) || 
      opt.currencyCode === String(currencyValue)
    );
    if (foundByValue) {
      // Return currency code if available, otherwise value
      return foundByValue.currencyCode || foundByValue.value || currencyValue;
    }
    
    // If not found, it might already be a currency code (like "INR", "USD")
    const foundByCode = currencyOptions.find(opt => 
      opt.currencyCode === String(currencyValue).toUpperCase()
    );
    if (foundByCode) {
      return foundByCode.currencyCode || foundByCode.value || currencyValue;
    }
    
    // If still not found, return the value as is (might already be a code)
    return currencyValue;
  };
  
  // Helper function to get unit label from value (ID or label)
  const getUnitLabel = (unitValue) => {
    if (!unitValue) return '';
    
    // First try to find by value (ID)
    const foundByValue = attributeUnitOptions.find(opt => 
      String(opt.value) === String(unitValue)
    );
    if (foundByValue) {
      return foundByValue.label || unitValue;
    }
    
    // If not found by ID, try to find by label (might already be a label)
    const foundByLabel = attributeUnitOptions.find(opt => 
      opt.label === String(unitValue)
    );
    if (foundByLabel) {
      return foundByLabel.label;
    }
    
    // If still not found, return the value as is (might already be a label)
    return unitValue;
  };
  
  // Helper function to find option by value or label
  // Supports both ID (for backward compatibility) and name (string) lookups
  const findOption = (options, val) => {
    if (!val || !options || options.length === 0) return null;
    // First try to find by label (name) - for string values
    const foundByLabel = options.find((o) => 
      String(o.label) === String(val) || 
      o.label === val
    );
    if (foundByLabel) return foundByLabel;
    // Fallback to ID lookup (for backward compatibility)
    return options.find((o) => 
      String(o.value) === String(val) || 
      Number(o.value) === Number(val) ||
      o.value === val
    ) || null;
  };
  
  // Legacy location states (for backward compatibility with existing code)
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  
  // Update legacy states when hooks update
  useEffect(() => {
    setCountries(rawCountries);
  }, [rawCountries]);
  
  useEffect(() => {
    setStates(rawStates);
  }, [rawStates]);
  
  useEffect(() => {
    setCities(rawCities);
  }, [rawCities]);

  // Country to Airports mapping
  const countryAirports = {
    "India": [
      "Mumbai (BOM)",
      "Delhi (DEL)",
      "Chennai (MAA)",
      "Kolkata (CCU)",
      "Bangalore (BLR)",
      "Hyderabad (HYD)",
      "Pune (PNQ)",
      "Ahmedabad (AMD)",
      "Cochin (COK)",
      "Goa (GOI)"
    ],
    "China": [
      "Beijing Capital (PEK)",
      "Shanghai Pudong (PVG)",
      "Guangzhou (CAN)",
      "Shenzhen (SZX)",
      "Chengdu (CTU)",
      "Xi'an (XIY)",
      "Hangzhou (HGH)",
      "Nanjing (NKG)"
    ],
    "USA": [
      "New York JFK (JFK)",
      "Los Angeles (LAX)",
      "Chicago O'Hare (ORD)",
      "Miami (MIA)",
      "San Francisco (SFO)",
      "Atlanta (ATL)",
      "Dallas (DFW)",
      "Seattle (SEA)"
    ],
    "UK": [
      "London Heathrow (LHR)",
      "London Gatwick (LGW)",
      "Manchester (MAN)",
      "Birmingham (BHX)",
      "Edinburgh (EDI)",
      "Glasgow (GLA)"
    ]
  };

  // Country to Sea Ports mapping
  const countryPorts = {
    "India": [
      "Mumbai Port",
      "Jawaharlal Nehru Port (Nhava Sheva)",
      "Chennai Port",
      "Kolkata Port",
      "Cochin Port",
      "Kandla Port",
      "Visakhapatnam Port",
      "Mormugao Port",
      "Paradip Port",
      "Tuticorin Port"
    ],
    "China": [
      "Shanghai Port",
      "Ningbo-Zhoushan Port",
      "Shenzhen Port",
      "Guangzhou Port",
      "Qingdao Port",
      "Tianjin Port",
      "Xiamen Port",
      "Dalian Port",
      "Yantian Port",
      "Hong Kong Port"
    ],
    "USA": [
      "Los Angeles Port",
      "Long Beach Port",
      "New York/New Jersey Port",
      "Savannah Port",
      "Houston Port",
      "Seattle Port",
      "Oakland Port",
      "Miami Port",
      "Charleston Port",
      "Norfolk Port"
    ],
    "UK": [
      "Port of London",
      "Port of Felixstowe",
      "Port of Southampton",
      "Port of Liverpool",
      "Port of Immingham",
      "Port of Tilbury",
      "Port of Hull",
      "Port of Dover"
    ]
  };

  // Get country name from country ID or name
  // Supports both ID (for backward compatibility) and name (string) inputs
  const getCountryName = (countryIdOrName) => {
    if (!countryIdOrName) return null;
    
    // If it's already a name (contains letters or not a pure number), return as is
    const countryStr = String(countryIdOrName).trim();
    if (isNaN(countryStr) || countryStr.includes(' ')) {
      // It's already a name, just verify it exists in our mappings
      if (countryAirports[countryStr] || countryPorts[countryStr]) {
        return countryStr;
      }
      // Try case-insensitive match
      const countryNameLower = countryStr.toLowerCase();
      for (const key in countryAirports) {
        if (key.toLowerCase() === countryNameLower) return key;
      }
      for (const key in countryPorts) {
        if (key.toLowerCase() === countryNameLower) return key;
      }
      return countryStr; // Return as is if not found in mappings
    }
    
    // It's an ID (number), look it up
    // First try to find in countryOptions (react-select format)
    const countryOption = countryOptions.find(opt => 
      String(opt.value) === String(countryIdOrName)
    );
    if (countryOption && countryOption.label) {
      const countryName = countryOption.label;
      // Check for exact match first
      if (countryAirports[countryName] || countryPorts[countryName]) {
        return countryName;
      }
      // Try case-insensitive match
      const countryNameLower = countryName.toLowerCase();
      for (const key in countryAirports) {
        if (key.toLowerCase() === countryNameLower) return key;
      }
      for (const key in countryPorts) {
        if (key.toLowerCase() === countryNameLower) return key;
      }
      return countryName;
    }
    
    // Fallback to countries array
    if (countries && countries.length > 0) {
      const country = countries.find(c => 
        String(c.country_id || c.id) === String(countryIdOrName)
      );
      if (country) {
        const countryName = country.country || country.country_name || country.name || country.label;
        if (countryName) {
          // Check for exact match first
          if (countryAirports[countryName] || countryPorts[countryName]) {
            return countryName;
          }
          // Try case-insensitive match
          const countryNameLower = countryName.toLowerCase();
          for (const key in countryAirports) {
            if (key.toLowerCase() === countryNameLower) return key;
          }
          for (const key in countryPorts) {
            if (key.toLowerCase() === countryNameLower) return key;
          }
          return countryName;
        }
      }
    }
    
    return null;
  };

  // Get airports for selected country (accepts both ID and name)
  const getAirportsForCountry = (countryIdOrName) => {
    if (!countryIdOrName) return [];
    const countryName = getCountryName(countryIdOrName);
    if (!countryName) return [];
    return countryAirports[countryName] || [];
  };

  // Get ports for selected country (accepts both ID and name)
  const getPortsForCountry = (countryIdOrName) => {
    if (!countryIdOrName) return [];
    const countryName = getCountryName(countryIdOrName);
    if (!countryName) return [];
    return countryPorts[countryName] || [];
  };

  useEffect(() => {
    if (!p2pLaunch?.p2p_transaction_id || p2pPrefillDone || isEditMode) return;
    const loadP2pPrefill = async () => {
      try {
        const res = await p2pTransactionService.detail(p2pLaunch.p2p_transaction_id);
        const tx = res?.data?.transaction;
        if (!tx) return;
        const amount = tx.estimated_amount != null ? Number(tx.estimated_amount) : null;
        setForm((prev) => ({
          ...prev,
          productName: p2pLaunch.product_title || tx.title || prev.productName,
          description: tx.description || prev.description,
          quantity: p2pLaunch.quantity || prev.quantity,
          currency: p2pLaunch.currency || tx.currency || prev.currency,
          minPrice:
            amount != null && !Number.isNaN(amount) ? String(amount) : prev.minPrice,
          maxPrice:
            amount != null && !Number.isNaN(amount) ? String(amount) : prev.maxPrice,
        }));
        setP2pPrefillDone(true);
      } catch (e) {
        console.warn('P2P prefill skipped', e);
      }
    };
    void loadP2pPrefill();
  }, [p2pLaunch, p2pPrefillDone, isEditMode]);

  // Prefill from marketplace product page (Order Now)
  useEffect(() => {
    if (isEditMode || p2pLaunch?.p2p_transaction_id) return;
    const productTitle = searchParams.get("product_title");
    const category = searchParams.get("category");
    const subCategory = searchParams.get("subCategory");
    const subSubCategory = searchParams.get("subSubCategory");
    const sourceProductId = searchParams.get("sourceProductId");
    if (!productTitle && !category && !sourceProductId) return;

    setForm((prev) => ({
      ...prev,
      productName: productTitle || prev.productName,
      productCategory: category || prev.productCategory,
      productSubCategory: subCategory || prev.productSubCategory,
      productSubSubCategory: subSubCategory || prev.productSubSubCategory,
      preferences: prev.preferences || "buy-directly",
    }));
  }, [searchParams, isEditMode, p2pLaunch]);

  // Prefill from home page Quick Buy Request draft
  useEffect(() => {
    if (isEditMode || searchParams.get("fromQuickBuy") !== "1") return;
    const draft = readQuickBuyRequestDraft();
    if (!draft) return;

    setForm((prev) => ({
      ...prev,
      productName: draft.productName || prev.productName,
      productCategory: draft.productCategory || prev.productCategory,
      productSubCategory: draft.productSubCategory || prev.productSubCategory,
      productSubSubCategory: draft.productSubSubCategory || prev.productSubSubCategory,
      quantity: draft.quantity || prev.quantity,
      unit: draft.unit || prev.unit,
      shippingAddress: draft.shippingAddress || draft.deliveryPlace || prev.shippingAddress,
      shippingCountry: draft.shippingCountry || prev.shippingCountry,
      shippingState: draft.shippingState || prev.shippingState,
      shippingCity: draft.shippingCity || prev.shippingCity,
      shippingPincode: draft.shippingPincode || prev.shippingPincode,
      preferences: draft.preferences || prev.preferences || "buy-directly",
      description: draft.description || prev.description,
    }));
    clearQuickBuyRequestDraft();
  }, [searchParams, isEditMode]);

  // Fetch main categories on component mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoadingCategories(true);
        const response = await CommonService.get("/master/master-categories");
        if (response && response.success && response.data) {
          const categoriesList = Array.isArray(response.data) 
            ? response.data 
            : (response.data.categories || response.data.master_categories || []);
          setCategories(categoriesList);
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
      } finally {
        setLoadingCategories(false);
      }
    };
    fetchCategories();
  }, []);

  // Map categories to react-select format
  const categoryOptions = categories.map(cat => {
    const categoryId = cat.category_id || cat.id;
    const categoryName = cat.category || cat.category_name || cat.name;
    return { value: String(categoryId), label: categoryName };
  });
  
  const subCategoryOptions = subCategories.map(sub => {
    const subCategoryId = sub.sub_category_id || sub.id;
    const subCategoryName = sub.sub_category || sub.sub_category_name || sub.name;
    return { value: String(subCategoryId), label: subCategoryName };
  });
  
  const subSubCategoryOptions = subSubCategories.map(subSub => {
    const subSubCategoryId = subSub.sub_sub_category_id || subSub.id;
    const subSubCategoryName = subSub.sub_sub_category || subSub.sub_sub_category_name || subSub.name;
    return { value: String(subSubCategoryId), label: subSubCategoryName };
  });
  
  // Unit options from API (use attributeUnitOptions from useProductDetailsMaster)
  const unitOptions = attributeUnitOptions || [];
  
  // Preferences options
  const preferencesOptions = [
    { value: "buy-with-help", label: "Buy with help from the platform's procurement team" },
    { value: "buy-directly", label: "Buy directly by posting my needs on the platform" }
  ];
  
  // Payment Terms options
  const paymentTermsOptions = [
    { value: "T/T", label: "T/T" },
    { value: "L/C", label: "L/C" },
    { value: "D/P", label: "D/P" },
    { value: "D/A", label: "D/A" },
    { value: "MoneyGram", label: "MoneyGram" },
    { value: "Credit Card", label: "Credit Card" },
    { value: "PayPal", label: "PayPal" },
    { value: "Western Union", label: "Western Union" },
    { value: "Cash", label: "Cash" }
  ];
  
  // Trade Terms (Incoterms) options
  const tradeTermsOptions = [
    { value: "FOB Destination", label: "FOB Destination" },
    { value: "FOB Shipping Point", label: "FOB Shipping Point" },
    { value: "FOB (Free on Board)", label: "FOB (Free on Board)" },
    { value: "CIF (Cost, Insurance, and Freight)", label: "CIF (Cost, Insurance, and Freight)" },
    { value: "CPT (Carriage Paid To)", label: "CPT (Carriage Paid To)" },
    { value: "DAP (Delivered at Place)", label: "DAP (Delivered at Place)" },
    { value: "DAT (Delivered at Terminal)", label: "DAT (Delivered at Terminal)" },
    { value: "Freight Prepaid", label: "Freight Prepaid" },
    { value: "Freight Collect", label: "Freight Collect" },
    { value: "DDP (Delivered Duty Paid)", label: "DDP (Delivered Duty Paid)" },
    { value: "DDU (Delivered Duty Unpaid)", label: "DDU (Delivered Duty Unpaid)" },
    { value: "CIP (Carriage and Insurance Paid To)", label: "CIP (Carriage and Insurance Paid To)" },
    { value: "FCA (Free Carrier)", label: "FCA (Free Carrier)" },
    { value: "EXW (Ex Works)", label: "EXW (Ex Works)" },
    { value: "Delivered Ex-Ship", label: "Delivered Ex-Ship" },
    { value: "FAS (Free Alongside Ship)", label: "FAS (Free Alongside Ship)" }
  ];
  
  // Shipping Method options
  const shippingMethodOptions = [
    { value: "Sea freight", label: "Sea freight" },
    { value: "Air freight", label: "Air freight" },
    { value: "Land freight", label: "Land freight" },
    { value: "Express", label: "Express" },
    { value: "Railways", label: "Railways" }
  ];
  
  // Destination Port/Airport options (dynamic based on shipping method and country)
  const getDestinationPortOptions = () => {
    // Only show options if both country and shipping method are selected
    if (!form.shippingCountry || !form.shippingMethod) return [];
    
    // Only show for Air freight or Sea freight
    if (form.shippingMethod !== "Air freight" && form.shippingMethod !== "Sea freight") {
      return [];
    }
    
    if (form.shippingMethod === "Air freight") {
      const airports = getAirportsForCountry(form.shippingCountry);
      return airports.map(airport => ({ value: airport, label: airport }));
    } else if (form.shippingMethod === "Sea freight") {
      const ports = getPortsForCountry(form.shippingCountry);
      return ports.map(port => ({ value: port, label: port }));
    }
    
    return [];
  };
  
  const destinationPortOptions = getDestinationPortOptions();
  
  // Check if destination port should be enabled
  const isDestinationPortEnabled = () => {
    // Must have country, shipping method, and valid options
    if (!form.shippingCountry) return false;
    if (form.shippingMethod !== "Air freight" && form.shippingMethod !== "Sea freight") return false;
    return destinationPortOptions.length > 0;
  };

  // Fetch company profile to get company name for file uploads
  useEffect(() => {
    const fetchCompanyProfile = async () => {
      try {
        const response = await CommonService.get('/company/my-profile');
        if (response && response.success && response.data) {
          const company = response.data;
          // Use company name, or fallback to a sanitized version
          const name = company.company_name || company.name || 'RFQ';
          setCompanyName(name);
        }
      } catch (error) {
        console.error('Error fetching company profile:', error);
        // Use default if fetch fails
        setCompanyName('RFQ');
      }
    };
    
    fetchCompanyProfile();
  }, []);

  // Fetch RFQ data when in edit mode
  useEffect(() => {
    const fetchRFQData = async () => {
      if (!isEditMode || !editRfqId) return;

      try {
        setLoadingRFQ(true);
        const response = await CommonService.get(`/rfq/${editRfqId}`);
        
        if (response && response.success && response.data) {
          const rfq = response.data;
          
          // Populate form with existing RFQ data
          // For shipping location fields, use the string values directly (they should be names now)
          // If they're still IDs (backward compatibility), we'll convert them in the useEffect hooks
          setForm({
            productName: rfq.product_name || "",
            productCategory: rfq.category_id ? String(rfq.category_id) : "",
            productSubCategory: rfq.sub_category_id ? String(rfq.sub_category_id) : "",
            productSubSubCategory: rfq.sub_sub_category_id ? String(rfq.sub_sub_category_id) : "",
            keywords: rfq.keywords || [],
            quantity: rfq.product_quantity ? String(rfq.product_quantity) : "",
            // Map unit ID to label for display
            unit: getUnitLabel(rfq.quantity_unit) || rfq.quantity_unit || "",
            // Map currency ID to currency code for display
            currency: getCurrencyLabel(rfq.target_price_currency) || rfq.target_price_currency || "",
            minPrice: rfq.target_price_min ? String(rfq.target_price_min) : "",
            maxPrice: rfq.target_price_max ? String(rfq.target_price_max) : "",
            tradeTerms: rfq.trade_terms || "",
            preferences: rfq.preferences || "",
            description: rfq.product_description || "",
            hsnCode: rfq.hsn_code || "",
            shippingMethod: rfq.preferred_shipping_method || "",
            destinationPort: rfq.destination_port || "",
            leadTimeDays: rfq.lead_time_days ? String(rfq.lead_time_days) : "0",
            paymentTerms: rfq.payment_terms || "",
            paymentMethod: rfq.payment_method || "",
            // Store as string - if it's a number (old data), convert to name in useEffect
            shippingCountry: rfq.shipping_country ? String(rfq.shipping_country) : "",
            shippingState: rfq.shipping_state ? String(rfq.shipping_state) : "",
            shippingCity: rfq.shipping_city ? String(rfq.shipping_city) : "",
            shippingAddress: rfq.shipping_address || "",
            shippingPincode: rfq.shipping_pincode || "",
            specialRequirements: rfq.special_requirements || "",
            buyerLogisticsRequested: Boolean(rfq.buyer_logistics_requested),
          });

          // Set uploaded file URLs if they exist
          if (rfq.uploaded_files && rfq.uploaded_files.length > 0) {
            console.log('Loading RFQ images:', {
              uploaded_files: rfq.uploaded_files,
              count: rfq.uploaded_files.length,
              first_url: rfq.uploaded_files[0]
            });
            setUploadedFileUrls(rfq.uploaded_files);
          } else {
            console.log('No uploaded files found in RFQ data');
          }
        } else {
          toast.error("Failed to load RFQ data");
        }
      } catch (error) {
        console.error("Error fetching RFQ data:", error);
        const errorMessage = error?.response?.data?.message || "Failed to load RFQ data";
        toast.error(errorMessage);
      } finally {
        setLoadingRFQ(false);
        // Reset location data loaded flag when starting a new fetch
        locationDataLoadedRef.current = false;
      }
    };

    fetchRFQData();
  }, [isEditMode, editRfqId]);

  // Load location data when country/state are set in edit mode (only once after RFQ data is loaded)
  // Convert country name to ID if needed (for backward compatibility with old ID-based data)
  useEffect(() => {
    if (isEditMode && form.shippingCountry && !loadingRFQ && !locationDataLoadedRef.current && rawCountries.length > 0) {
      // Check if shippingCountry is a name (string) or ID (number)
      const countryValue = form.shippingCountry;
      let countryId = null;
      
      // Try to find by label (name) first in rawCountries
      const foundCountry = rawCountries.find(c => {
        const label = c.label || c.country_name || c.country || '';
        return label === countryValue;
      });
      if (foundCountry) {
        countryId = foundCountry.id || foundCountry.country_id || foundCountry.value;
      } else if (!isNaN(countryValue)) {
        // If it's a number (old ID-based data), use it directly
        countryId = countryValue;
        // Convert the stored value to name for display
        const countryForDisplay = rawCountries.find(c => {
          const id = c.id || c.country_id || c.value;
          return String(id) === String(countryValue);
        });
        if (countryForDisplay) {
          const countryName = countryForDisplay.label || countryForDisplay.country_name || countryForDisplay.country || '';
          setForm(prev => ({ ...prev, shippingCountry: countryName }));
        }
      }
      
      if (countryId && rawStates.length === 0) {
        updateListsForCountry(countryId);
        locationDataLoadedRef.current = true;
      } else {
        // States already loaded or no country found, mark as loaded
        locationDataLoadedRef.current = true;
      }
    }
  }, [isEditMode, form.shippingCountry, loadingRFQ, rawStates.length, rawCountries.length]);

  // Load cities when state is set in edit mode (only once after states are loaded)
  // Convert state name to ID if needed (for backward compatibility with old ID-based data)
  useEffect(() => {
    if (isEditMode && form.shippingCountry && form.shippingState && !loadingRFQ && rawStates.length > 0 && locationDataLoadedRef.current) {
      // Find country ID from country name in rawCountries
      const foundCountry = rawCountries.find(c => {
        const label = c.label || c.country_name || c.country || '';
        return label === form.shippingCountry;
      });
      const countryId = foundCountry ? (foundCountry.id || foundCountry.country_id || foundCountry.value) : null;
      
      // Find state ID from state name in rawStates
      const foundState = rawStates.find(s => {
        const label = s.label || s.state_name || s.state || '';
        return label === form.shippingState;
      });
      let stateId = foundState ? (foundState.id || foundState.state_id || foundState.value) : null;
      
      // If state not found by name, try as ID (backward compatibility)
      if (!stateId && !isNaN(form.shippingState)) {
        stateId = form.shippingState;
        // Convert the stored value to name for display
        const stateForDisplay = rawStates.find(s => {
          const id = s.id || s.state_id || s.value;
          return String(id) === String(form.shippingState);
        });
        if (stateForDisplay) {
          const stateName = stateForDisplay.label || stateForDisplay.state_name || stateForDisplay.state || '';
          setForm(prev => ({ ...prev, shippingState: stateName }));
        }
      }
      
      // Only load cities if they haven't been loaded yet for this state
      if (stateId && countryId && rawCities.length === 0) {
        updateListsForState(countryId, stateId);
      }
    }
  }, [isEditMode, form.shippingCountry, form.shippingState, loadingRFQ, rawStates.length, rawCities.length, rawCountries.length]);

  // Convert city from ID to name in edit mode (for backward compatibility with old ID-based data)
  useEffect(() => {
    if (isEditMode && form.shippingCity && !loadingRFQ && rawCities.length > 0) {
      // Check if shippingCity is a name (string with letters) or ID (number)
      const cityValue = form.shippingCity;
      
      // If it's a number (old ID-based data), convert to name
      if (!isNaN(cityValue) && !cityValue.includes(' ')) {
        const foundCity = rawCities.find(c => {
          const id = c.id || c.city_id || c.value;
          return String(id) === String(cityValue);
        });
        if (foundCity) {
          const cityName = foundCity.label || foundCity.city_name || foundCity.city || '';
          setForm(prev => ({ ...prev, shippingCity: cityName }));
        }
      }
    }
  }, [isEditMode, form.shippingCity, loadingRFQ, rawCities.length]);

  // Update currency when currencyOptions becomes available in edit mode
  useEffect(() => {
    if (isEditMode && !loadingRFQ && form.currency && currencyOptions && currencyOptions.length > 0) {
      // Check if currency needs to be mapped
      const currentCurrency = form.currency;
      const foundCurrency = findOption(currencyOptions, currentCurrency);
      if (!foundCurrency) {
        // Currency not found, try to map it
        const mappedCurrency = getCurrencyLabel(currentCurrency);
        if (mappedCurrency && mappedCurrency !== currentCurrency) {
          console.log('Updating currency from', currentCurrency, 'to', mappedCurrency);
          setForm(prev => ({ ...prev, currency: mappedCurrency }));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, form.currency, loadingRFQ, currencyOptions]);

  // Update unit when attributeUnitOptions becomes available in edit mode
  useEffect(() => {
    if (isEditMode && !loadingRFQ && form.unit && attributeUnitOptions && attributeUnitOptions.length > 0 && !uomLoading) {
      // Check if unit needs to be mapped
      const currentUnit = form.unit;
      const foundUnit = findOption(attributeUnitOptions, currentUnit);
      if (!foundUnit) {
        // Unit not found, try to map it
        const mappedUnit = getUnitLabel(currentUnit);
        if (mappedUnit && mappedUnit !== currentUnit) {
          console.log('Updating unit from', currentUnit, 'to', mappedUnit);
          setForm(prev => ({ ...prev, unit: mappedUnit }));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, form.unit, loadingRFQ, attributeUnitOptions, uomLoading]);

  // Location data is now handled by useLocationMaster hook
  // No need for manual fetching - the hook handles countries, states, and cities automatically

  // Fetch sub-categories when category changes
  useEffect(() => {
    const fetchSubCategories = async () => {
      if (!form.productCategory) {
        setSubCategories([]);
        setSubSubCategories([]);
        return;
      }
      
      try {
        setLoadingSubCategories(true);
        const categoryId = form.productCategory;
        const response = await CommonService.get(`/master/master-sub-categories/${categoryId}`);
        if (response && response.success && response.data) {
          const subCategoriesList = Array.isArray(response.data) 
            ? response.data 
            : [response.data];
          setSubCategories(subCategoriesList);
        } else {
          setSubCategories([]);
        }
        // Clear sub-sub-categories when category changes
        setSubSubCategories([]);
      } catch (error) {
        console.error("Error fetching sub-categories:", error);
        setSubCategories([]);
      } finally {
        setLoadingSubCategories(false);
      }
    };
    fetchSubCategories();
  }, [form.productCategory]);

  // Fetch sub-sub-categories when sub-category changes
  useEffect(() => {
    const fetchSubSubCategories = async () => {
      if (!form.productSubCategory) {
        setSubSubCategories([]);
        return;
      }
      
      try {
        setLoadingSubSubCategories(true);
        const subCategoryId = form.productSubCategory;
        const response = await CommonService.get(`/master/master-sub-sub-categories/${subCategoryId}`);
        if (response && response.success && response.data) {
          const subSubCategoriesList = Array.isArray(response.data) 
            ? response.data 
            : [response.data];
          setSubSubCategories(subSubCategoriesList);
        } else {
          setSubSubCategories([]);
        }
      } catch (error) {
        console.error("Error fetching sub-sub-categories:", error);
        setSubSubCategories([]);
      } finally {
        setLoadingSubSubCategories(false);
      }
    };
    fetchSubSubCategories();
  }, [form.productSubCategory]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const updated = { ...prev, [name]: value };
      // Clear destination port when country changes
      if (name === "shippingCountry") {
        updated.destinationPort = "";
        updated.shippingState = "";
        updated.shippingCity = "";
      }
      // Clear city when state changes
      if (name === "shippingState") {
        updated.shippingCity = "";
      }
      // Clear sub-categories when category changes
      if (name === "productCategory") {
        updated.productSubCategory = "";
        updated.productSubSubCategory = "";
      }
      // Clear sub-sub-categories when sub-category changes
      if (name === "productSubCategory") {
        updated.productSubSubCategory = "";
      }
      return updated;
    });
  };

  // Handle location changes with cascading updates
  // Store the label (name) instead of ID for Country, State, and City
  const handleLocationChange = (type, selected) => {
    // Store the label (name) as the value, but keep the ID for cascading dropdowns
    const selectedId = selected?.value || "";
    const selectedLabel = selected?.label || "";
    
    setForm((prev) => {
      const updated = { ...prev };
      
      if (type === "shippingCountry") {
        // Store the country name (label) instead of ID
        updated.shippingCountry = selectedLabel;
        updated.shippingState = "";
        updated.shippingCity = "";
        updated.destinationPort = "";
        // Update states and cities lists using the ID
        if (selectedId) {
          updateListsForCountry(selectedId);
        } else {
          updateListsForCountry(null);
        }
      } else if (type === "shippingState") {
        // Store the state name (label) instead of ID
        updated.shippingState = selectedLabel;
        updated.shippingCity = "";
        // Update cities list - pass both countryId and stateId
        // Need to find country ID from the stored country name
        // Try to find in countryOptions first, then fallback to rawCountries
        let countryId = null;
        const countryOption = countryOptions.find(opt => opt.label === prev.shippingCountry);
        if (countryOption) {
          countryId = countryOption.value;
        } else {
          // Fallback to rawCountries if countryOptions not available
          const foundCountry = rawCountries.find(c => {
            const label = c.label || c.country_name || c.country || '';
            return label === prev.shippingCountry;
          });
          if (foundCountry) {
            countryId = foundCountry.id || foundCountry.country_id || foundCountry.value;
          }
        }
        if (selectedId && countryId) {
          updateListsForState(countryId, selectedId);
        } else {
          updateListsForState(null, null);
        }
      } else if (type === "shippingCity") {
        // Store the city name (label) instead of ID
        // Always use the label, never fall back to ID
        if (selected) {
          if (selectedLabel) {
            // Store the label (city name)
            updated.shippingCity = selectedLabel;
          } else {
            // If label is missing, log warning and don't store (shouldn't happen with proper options)
            console.warn('City selected but label is missing:', selected);
            updated.shippingCity = "";
          }
        } else {
          // If selected is null/undefined (cleared), set to empty string
          updated.shippingCity = "";
        }
      }
      
      return updated;
    });
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    // Check total images (new files + existing URLs)
    const totalImages = uploadedFiles.length + uploadedFileUrls.length;
    if (totalImages >= 3) {
      toast.error("Maximum 3 images allowed");
      e.target.value = '';
      return;
    }
    
    // Validate file types
    const invalidFiles = imageFiles.filter(file => !file.type.startsWith('image/'));
    if (invalidFiles.length > 0) {
      toast.error("Please select only image files");
      e.target.value = '';
      return;
    }
    
    // Validate file size (max 5MB per image)
    const maxSize = 5 * 1024 * 1024; // 5MB
    const oversizedFiles = imageFiles.filter(file => file.size > maxSize);
    if (oversizedFiles.length > 0) {
      toast.error("Image size should not exceed 5MB");
      e.target.value = '';
      return;
    }
    
    const remainingSlots = 3 - totalImages;
    const newFiles = imageFiles.slice(0, remainingSlots);
    setUploadedFiles(prev => [...prev, ...newFiles]);
    e.target.value = '';
  };

  // Helper function to get full image URL from access path
  const getImageUrl = (accessPath) => {
    if (!accessPath) return '';
    
    // If already a full URL, return as is
    if (accessPath.startsWith('http://') || accessPath.startsWith('https://')) {
      return accessPath;
    }
    
    const baseUrl = 'http://localhost:3000';
    
    // Handle old path formats that might have Windows absolute paths
    // Convert paths like /C:/usr/local/... to /rfq-images/...
    if (accessPath.includes('C:/usr/local/b2b/buyer/product image') || accessPath.includes('C:\\usr\\local\\b2b\\buyer\\product image')) {
      // Extract RFQ ID and filename from old path format
      const match = accessPath.match(/product image[\/\\](\d+)[\/\\]([^\/\\]+)$/);
      if (match) {
        const rfqId = match[1];
        const fileName = match[2];
        return `${baseUrl}/rfq-images/${rfqId}/${fileName}`;
      }
      // Handle temp folder
      const tempMatch = accessPath.match(/product image[\/\\]temp[\/\\]([^\/\\]+)$/);
      if (tempMatch) {
        const fileName = tempMatch[1];
        return `${baseUrl}/rfq-images/temp/${fileName}`;
      }
    }
    
    // If relative path, prepend base URL (static files are served from root, not /api/v1)
    // Ensure the path starts with / and doesn't have :3000 in it
    let cleanPath = accessPath;
    if (cleanPath.startsWith(':3000')) {
      cleanPath = cleanPath.replace(/^:3000/, '');
    }
    if (!cleanPath.startsWith('/')) {
      cleanPath = '/' + cleanPath;
    }
    return `${baseUrl}${cleanPath}`;
  };

  // Remove new file (not yet uploaded)
  const removeFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, idx) => idx !== index));
  };

  // Remove existing file URL (already uploaded)
  const removeFileUrl = (index) => {
    setUploadedFileUrls(prev => prev.filter((_, idx) => idx !== index));
  };

  // Upload file to backend and return URL
  const uploadFile = async (file, fileType = 'product_image', category = 'product') => {
    try {
      // Use company name from profile, or fallback to 'RFQ'
      const uploadCompanyName = companyName || 'RFQ';
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', category); // Use 'product' category (validated by backend)
      formData.append('companyName', uploadCompanyName);
      formData.append('fileType', fileType);
      
      // Pass RFQ ID if in edit mode (for RFQ-specific folder path)
      if (isEditMode && editRfqId) {
        formData.append('rfqId', editRfqId);
      }

      const response = await CommonService.post('/upload/single', formData);

      if (response && response.success && response.data) {
        // Return the access path (URL) from the response
        return response.data.accessPath || response.data.filePath || '';
      }
      throw new Error(response?.message || 'Upload failed');
    } catch (error) {
      console.error('File upload error:', error);
      const errorMessage = error?.response?.data?.message || error?.response?.data?.errors?.[0] || error?.message || `Failed to upload ${fileType}`;
      toast.error(errorMessage);
      throw error;
    }
  };

  const addKeyword = () => {
    const v = keywordInput.trim();
    if (!v) return;
    if (!form.keywords.includes(v)) {
      setForm((prev) => ({ ...prev, keywords: [...prev.keywords, v] }));
    }
    setKeywordInput("");
  };

  const removeKeyword = (k) => {
    setForm((prev) => ({ ...prev, keywords: prev.keywords.filter((x) => x !== k) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    const incompleteFields = getIncompleteFields();
    if (incompleteFields.length > 0) {
      toast.error(`Please fill in the required fields: ${incompleteFields.join(', ')}`);
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Upload new files first
      // For new RFQs: files will be uploaded to temp folder (no RFQ ID yet)
      // For edit mode: files will be uploaded to RFQ-specific folder (RFQ ID available)
      const newFileUrls = [];
      for (const file of uploadedFiles) {
        try {
          // uploadFile will automatically use RFQ ID if in edit mode
          const fileUrl = await uploadFile(file, 'product_image', 'product');
          if (fileUrl && fileUrl.trim() !== '') {
            newFileUrls.push(fileUrl);
            console.log('Successfully uploaded file, URL:', fileUrl);
          } else {
            console.warn('File uploaded but no URL returned:', file.name);
          }
        } catch (error) {
          console.error('Error uploading file:', file.name, error);
          toast.error(`Failed to upload ${file.name}. Please try again.`);
          // Continue with other files even if one fails
        }
      }
      
      // If some files failed to upload, warn the user
      if (newFileUrls.length < uploadedFiles.length) {
        toast.warning(`${uploadedFiles.length - newFileUrls.length} file(s) failed to upload. Continuing with ${newFileUrls.length} successful upload(s).`);
      }

      // Combine new file URLs with existing URLs
      const allFileUrls = [...uploadedFileUrls, ...newFileUrls];
      
      // Debug: Log file URLs
      console.log('Uploaded files summary:', {
        newFiles: uploadedFiles.length,
        existingUrls: uploadedFileUrls.length,
        newFileUrls: newFileUrls.length,
        allFileUrls: allFileUrls,
        allFileUrlsLength: allFileUrls.length,
        allFileUrlsDetails: allFileUrls.map((url, idx) => ({ index: idx, url, type: typeof url }))
      });
      
      // Ensure allFileUrls is always an array (even if empty)
      if (!Array.isArray(allFileUrls)) {
        console.error('allFileUrls is not an array!', allFileUrls);
        toast.error('Error preparing file URLs. Please try again.');
        setIsSubmitting(false);
        return;
      }

      // Prepare RFQ payload
      const sourceProductId = searchParams.get("sourceProductId");
      const rfqPayload = {
        product_name: form.productName,
        category_id: form.productCategory ? parseInt(form.productCategory) : null,
        sub_category_id: form.productSubCategory ? parseInt(form.productSubCategory) : null,
        sub_sub_category_id: form.productSubSubCategory ? parseInt(form.productSubSubCategory) : null,
        keywords: form.keywords || [],
        product_quantity: form.quantity ? parseFloat(form.quantity) : null,
        quantity_unit: form.unit || null,
        target_price_currency: form.currency || null,
        target_price_min: form.minPrice ? parseFloat(form.minPrice) : null,
        target_price_max: form.maxPrice ? parseFloat(form.maxPrice) : null,
        trade_terms: form.tradeTerms || null,
        preferences: form.preferences || null,
        source_product_id: sourceProductId ? parseInt(sourceProductId, 10) : null,
        product_description: form.description || null,
        hsn_code: form.hsnCode || null,
        uploaded_files: allFileUrls,
        preferred_shipping_method: form.shippingMethod || null,
        lead_time_days: form.leadTimeDays ? parseInt(form.leadTimeDays) : null,
        payment_terms: form.paymentTerms || null,
        payment_method: form.paymentMethod || null,
        destination_port: form.destinationPort || null,
        // Store as string names instead of IDs
        shipping_country: form.shippingCountry || null,
        shipping_state: form.shippingState || null,
        shipping_city: form.shippingCity || null,
        shipping_address: form.shippingAddress || null,
        shipping_pincode: form.shippingPincode || null,
        special_requirements: form.specialRequirements || null,
        buyer_logistics_requested: Boolean(form.buyerLogisticsRequested),
        status: 'pending'
      };

      // Debug: Log the complete payload before sending
      console.log('RFQ Payload being sent:', {
        ...rfqPayload,
        uploaded_files_count: rfqPayload.uploaded_files?.length || 0,
        uploaded_files: rfqPayload.uploaded_files
      });

      // Submit RFQ to backend (POST for create, PUT for update)
      let response;
      if (isEditMode && editRfqId) {
        response = await CommonService.put(`/rfq/${editRfqId}`, rfqPayload);
      } else {
        response = await CommonService.post('/rfq', rfqPayload);
      }
      
      if (response && response.success) {
        const escalation = response?.data?.procurement_escalation;
        if (!isEditMode && escalation?.service_request_id) {
          toast.success(
            'RFQ submitted. Your request was sent to the platform procurement team for support.',
          );
        } else if (isEditMode && escalation?.service_request_id) {
          toast.success(
            'RFQ updated. The platform procurement team has been notified for managed sourcing.',
          );
        } else {
          toast.success(isEditMode ? 'RFQ updated successfully!' : 'RFQ submitted successfully!');
        }
        const rfqId =
          response?.data?.rfqId ??
          response?.data?.id ??
          response?.data?.rfq_id ??
          editRfqId;
        if (!isEditMode && p2pLaunch?.p2p_transaction_id && rfqId) {
          const linked = await completeP2pRfqLinkAfterModuleCreate({
            searchParams,
            p2pLaunchContext: p2pLaunch,
            sourceModule: 'marketplace',
            sourceRfqId: rfqId,
            sourceRfqTitle: form.productName || null,
            sourceRfqStatus: 'pending',
            navigate,
          });
          if (linked) return;
        }
        navigate("/buyer-rfq");
      } else {
        throw new Error(response?.message || (isEditMode ? 'Failed to update RFQ' : 'Failed to submit RFQ'));
      }
    } catch (error) {
      console.error('Error submitting RFQ:', error);
      const errorMessage = error?.response?.data?.message || error?.response?.data?.errors?.[0] || error?.message || 'Failed to submit RFQ. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate("/buyer-profile");
  };

  // Check which required fields are incomplete
  const getIncompleteFields = () => {
    const incomplete = [];
    if (!form.productName) incomplete.push("Product Name");
    if (!form.quantity) incomplete.push("Reqd Product Qty");
    // Check both new files and existing URLs for images
    if (uploadedFiles.length === 0 && uploadedFileUrls.length === 0) incomplete.push("Product Images");
    if (!form.minPrice || !form.maxPrice) incomplete.push("Target Price");
    if (!form.description) incomplete.push("Product Description");
    if (form.keywords.length === 0) incomplete.push("Keywords");
    return incomplete;
  };

  const incompleteFields = getIncompleteFields();
  const isComplete = incompleteFields.length === 0;

  return (
    <div className="buyer-rfq-form container-fluid">
      <div className="rfq-header-actions">
        <div>
          <h1 className="rfq-title">{isEditMode ? "Edit RFQ Details" : "Tell Us Exactly What You Need"}</h1>
          <p className="rfq-subtitle">
            {isEditMode 
              ? "Update your RFQ details below. Make sure all information is accurate."
              : "Suppliers respond better to detailed requests. Provide specs, quantity, and delivery details."}
          </p>
        </div>
        <div className="rfq-actions">
          <button type="button" className="rfq-back-btn" onClick={handleBack}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m12 19-7-7 7-7"></path>
              <path d="M19 12H5"></path>
            </svg>
            Back
          </button>
          <button type="submit" form="rfq-form" className="btn btn-primary" disabled={isSubmitting || loadingRFQ}>
            {isSubmitting ? (isEditMode ? "Updating..." : "Submitting...") : (isEditMode ? "Update" : "Submit")}
          </button>
        </div>
      </div>

      {loadingRFQ ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading RFQ data...</span>
          </div>
          <p className="mt-3">Loading RFQ details...</p>
        </div>
      ) : (
      <div className="row">
        <main className="col-12 col-lg-8">
          <form id="rfq-form" onSubmit={handleSubmit}>
            {/* Product Details Section */}
            <div className="rfq-main-content">
              <div className="rfq-section">
                <h6 className="rfq-section-heading">Product Details</h6>
                
                <div className="rfq-field">
                  <div className="row">
                    <div className="col-12 col-md-6 col-lg-6">
                      <input
                        type="text"
                        name="productName"
                        value={form.productName}
                        onChange={onChange}
                        className="input-pill"
                        placeholder="Product Name"
                        required
                      />
                    </div>
                    <div className="col-12 col-md-6 col-lg-6">
                      <Select
                        className="input-pill p-0"
                        placeholder={loadingCategories ? "Loading..." : "Product Category"}
                        options={categoryOptions}
                        value={findOption(categoryOptions, form.productCategory)}
                        onChange={(opt) => {
                          onChange({ target: { name: "productCategory", value: opt?.value || "" } });
                        }}
                        isDisabled={loadingCategories}
                        isClearable
                      />
                    </div>
                    </div>
                    </div>

                <div className="rfq-field">
                  <div className="row">
                    <div className="col-12 col-md-4 col-lg-4">
                      <Select
                        className="input-pill p-0"
                        placeholder={
                          loadingSubCategories 
                            ? "Loading..." 
                            : !form.productCategory 
                            ? "Select Category First" 
                            : "Product Sub Category"
                        }
                        options={subCategoryOptions}
                        value={findOption(subCategoryOptions, form.productSubCategory)}
                        onChange={(opt) => {
                          onChange({ target: { name: "productSubCategory", value: opt?.value || "" } });
                        }}
                        isDisabled={!form.productCategory || loadingSubCategories}
                        isClearable
                      />
                    </div>
                    <div className="col-12 col-md-4 col-lg-4">
                      <Select
                        className="input-pill p-0"
                        placeholder={
                          loadingSubSubCategories 
                            ? "Loading..." 
                            : !form.productSubCategory 
                            ? "Select Sub Category First" 
                            : "Product Sub - Sub Category"
                        }
                        options={subSubCategoryOptions}
                        value={findOption(subSubCategoryOptions, form.productSubSubCategory)}
                        onChange={(opt) => {
                          onChange({ target: { name: "productSubSubCategory", value: opt?.value || "" } });
                        }}
                        isDisabled={!form.productSubCategory || loadingSubSubCategories}
                        isClearable
                      />
                    </div>
                    <div className="col-12 col-md-4 col-lg-4">
                      <input
                        type="text"
                        name="hsnCode"
                        value={form.hsnCode}
                        onChange={onChange}
                        className="input-pill"
                        placeholder="HSN Code"
                        maxLength="50"
                      />
                    </div>
                  </div>
                </div>

                <div className="rfq-field">
                  <div className="row">
                    <div className="col-12 col-md-12 col-lg-12">
                      <div className="rfq-price-group">
                        <button type="button" className="btn-currency">
                          Target Price Range
                        </button>
                        <Select
                          className="input-pill p-0 currency-select"
                          placeholder="Currency"
                          options={currencyOptions}
                          value={findOption(currencyOptions, form.currency)}
                          onChange={(opt) => {
                            onChange({ target: { name: "currency", value: opt?.value || "" } });
                          }}
                          isClearable
                        />
                        <input
                          type="text"
                          name="minPrice"
                          value={form.minPrice}
                          onChange={onChange}
                          className="input-pill price-input"
                          placeholder="Min Price"
                          required
                        />
                        <input
                          type="text"
                          name="maxPrice"
                          value={form.maxPrice}
                          onChange={onChange}
                          className="input-pill price-input"
                          placeholder="Max Price"
                          required
                        />
                        <input
                          className="input-pill keyword-input"
                          placeholder="Add Keywords"
                          value={keywordInput}
                          onChange={(e) => setKeywordInput(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              addKeyword();
                            }
                          }}
                        />
                        <button type="button" className="btn-add" onClick={addKeyword}>+ Add</button>
                      </div>
                      {form.keywords.length > 0 && (
                        <div className="tags">
                          {form.keywords.map((k) => (
                            <span className="tag" key={k}>
                              {k}{" "}
                              <button type="button" className="tag-x" onClick={() => removeKeyword(k)}>
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="rfq-field">
                  <div className="row">
                    <div className="col-12 col-md-3 col-lg-3">
                      <input
                        type="text"
                        name="quantity"
                        value={form.quantity}
                        onChange={onChange}
                        className="input-pill"
                        placeholder="Reqd Product Qty"
                        required
                      />
                    </div>
                    <div className="col-12 col-md-3 col-lg-3">
                      <Select
                        className="input-pill p-0"
                        placeholder={uomLoading ? "Loading Units..." : "Unit"}
                        options={unitOptions}
                        value={findOption(unitOptions, form.unit)}
                        onChange={(opt) => {
                          onChange({ target: { name: "unit", value: opt?.value || "" } });
                        }}
                        isDisabled={uomLoading}
                        isClearable
                      />
                    </div>
                    <div className="col-12 col-md-6 col-lg-6">
                      <Select
                        className="input-pill p-0 preferences-select"
                        placeholder="Choose your procurement method"
                        options={preferencesOptions}
                        value={findOption(preferencesOptions, form.preferences)}
                        onChange={(opt) => {
                          onChange({ target: { name: "preferences", value: opt?.value || "" } });
                        }}
                        isClearable
                        required
                      />
                      {form.preferences === "buy-with-help" ? (
                        <p className="text-muted small mt-2 mb-0">
                          Your RFQ is sent to the platform procurement team for managed sourcing.
                          It will not appear in the public seller inbox until the team progresses it.
                        </p>
                      ) : null}
                    </div>
                  </div>
                </div>

                <div className="rfq-field rfq-field-description">
                  <div className="row">
                    <div className="col-12 col-md-12 col-lg-12">
                      <textarea
                        name="description"
                        value={form.description}
                        onChange={onChange}
                        className="input-area"
                        rows="2"
                        placeholder="Product Description"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="rfq-field">
                  <div className="file-upload-component">
                    <label className="file-box">
                      <div className="file-content">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="upload-icon"
                        >
                          <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path>
                        </svg>
                        <span className="choose-text">
                          Upload product images ({(uploadedFiles.length + uploadedFileUrls.length)}/3)
                        </span>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleFileChange}
                        disabled={(uploadedFiles.length + uploadedFileUrls.length) >= 3}
                      />
                    </label>
                    {(uploadedFiles.length > 0 || uploadedFileUrls.length > 0) && (
                      <div className="upload-preview-container" style={{ marginTop: '15px', display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                        {/* Display existing uploaded images */}
                        {uploadedFileUrls.map((url, idx) => {
                          const imageUrl = getImageUrl(url);
                          console.log(`Image ${idx + 1} URL:`, { original: url, processed: imageUrl });
                          return (
                            <div key={`url-${idx}`} style={{ position: 'relative', width: '120px', height: '120px', borderRadius: '8px', overflow: 'hidden', border: '2px solid #e0e0e0' }}>
                              <img
                                src={imageUrl}
                                alt={`Uploaded ${idx + 1}`}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                onError={(e) => {
                                  // Use a data URI for placeholder instead of external URL
                                  e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjEyMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTIwIiBoZWlnaHQ9IjEyMCIgZmlsbD0iI2Y1ZjVmNSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTIiIGZpbGw9IiM5OTk5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBFcnJvcjwvdGV4dD48L3N2Zz4=';
                                  console.error('Failed to load image:', { original: url, processed: imageUrl, error: 'Image load failed' });
                                }}
                              />
                            <button
                              type="button"
                              className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1"
                              style={{
                                borderRadius: '50%',
                                width: '28px',
                                height: '28px',
                                padding: 0,
                                lineHeight: '1',
                                fontSize: '18px',
                                fontWeight: 'bold',
                                zIndex: 10,
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                border: '1px solid #fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                              onClick={() => removeFileUrl(idx)}
                              title="Remove image"
                            >
                              ×
                            </button>
                          </div>
                          );
                        })}
                        {/* Display new files with preview */}
                        {uploadedFiles.map((file, idx) => {
                          const previewUrl = URL.createObjectURL(file);
                          return (
                            <div key={`file-${idx}`} style={{ position: 'relative', width: '120px', height: '120px', borderRadius: '8px', overflow: 'hidden', border: '2px solid #e0e0e0' }}>
                              <img
                                src={previewUrl}
                                alt={file.name}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                              <button
                                type="button"
                                className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1"
                                style={{
                                  borderRadius: '50%',
                                  width: '28px',
                                  height: '28px',
                                  padding: 0,
                                  lineHeight: '1',
                                  fontSize: '18px',
                                  fontWeight: 'bold',
                                  zIndex: 10,
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                  border: '1px solid #fff',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                                onClick={() => {
                                  URL.revokeObjectURL(previewUrl);
                                  removeFile(idx);
                                }}
                                title="Remove image"
                              >
                                ×
                              </button>
                              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.6)', color: '#fff', padding: '4px 8px', fontSize: '11px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {file.name}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Payment & Shipping Details Section */}
            <div className="rfq-main-content">
              <div className="rfq-section">
                <h6 className="rfq-section-heading">Payment & Shipping Details</h6>

                <div className="rfq-field">
                  <div className="row">
                    <div className="col-12 col-md-6 col-lg-6">
                      <Select
                        className="input-pill p-0"
                        placeholder="Payment Terms"
                        options={paymentTermsOptions}
                        value={findOption(paymentTermsOptions, form.paymentTerms)}
                        onChange={(opt) => {
                          onChange({ target: { name: "paymentTerms", value: opt?.value || "" } });
                        }}
                        isClearable
                      />
                    </div>
                    <div className="col-12 col-md-6 col-lg-6">
                      <Select
                        className="input-pill p-0"
                        placeholder="Incoterms"
                        options={tradeTermsOptions}
                        value={findOption(tradeTermsOptions, form.tradeTerms)}
                        onChange={(opt) => {
                          onChange({ target: { name: "tradeTerms", value: opt?.value || "" } });
                        }}
                        isClearable
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="rfq-field">
                  <div className="row">
                    <div className="col-12 col-md-12 col-lg-12">
                      <input
                        type="text"
                        name="shippingAddress"
                        value={form.shippingAddress}
                        onChange={onChange}
                        className="input-pill"
                        placeholder="Shipping Address"
                      />
                    </div>
                  </div>
                </div>

                <div className="rfq-field">
                  <div className="row">
                    <div className="col-12 col-md-3 col-lg-3">
                      <Select
                        className="input-pill p-0"
                        placeholder="Country"
                        options={countryOptions}
                        value={findOption(countryOptions, form.shippingCountry)}
                        onChange={(selected) => {
                          handleLocationChange("shippingCountry", selected);
                        }}
                        isClearable
                      />
                    </div>
                    <div className="col-12 col-md-3 col-lg-3">
                      <Select
                        className="input-pill p-0"
                        placeholder={!form.shippingCountry ? "Select Country First" : "State"}
                        options={stateOptions}
                        value={findOption(stateOptions, form.shippingState)}
                        onChange={(selected) => {
                          handleLocationChange("shippingState", selected);
                        }}
                        isDisabled={!form.shippingCountry}
                        isClearable
                      />
                    </div>
                    <div className="col-12 col-md-3 col-lg-3">
                      <Select
                        className="input-pill p-0"
                        placeholder={!form.shippingState ? "Select State First" : "City"}
                        options={cityOptions}
                        value={findOption(cityOptions, form.shippingCity)}
                        onChange={(selected) => {
                          handleLocationChange("shippingCity", selected);
                        }}
                        isDisabled={!form.shippingState}
                        isClearable
                      />
                    </div>
                    <div className="col-12 col-md-3 col-lg-3">
                      <input
                        type="text"
                        name="shippingPincode"
                        value={form.shippingPincode}
                        onChange={onChange}
                        className="input-pill"
                        placeholder="Pincode"
                        maxLength="10"
                      />
                    </div>
                  </div>
                </div>

                <div className="rfq-field">
                  <div className="row">
                    <div className="col-12 col-md-6 col-lg-6">
                      <Select
                        className="input-pill p-0"
                        placeholder="Preferred Shipping Method"
                        options={shippingMethodOptions}
                        value={findOption(shippingMethodOptions, form.shippingMethod)}
                        onChange={(opt) => {
                          const value = opt?.value || "";
                          onChange({ target: { name: "shippingMethod", value } });
                          // Clear destination port when shipping method changes
                          if (!value || (value !== "Air freight" && value !== "Sea freight")) {
                            setForm(prev => ({ ...prev, destinationPort: "" }));
                          }
                        }}
                        isClearable
                      />
                    </div>
                    {(form.shippingMethod === "Air freight" || form.shippingMethod === "Sea freight") && (
                      <div className="col-12 col-md-6 col-lg-6">
                        <Select
                          className="input-pill p-0"
                          placeholder={
                            !form.shippingCountry 
                              ? "Select Country First" 
                              : !form.shippingMethod
                              ? "Select Shipping Method First"
                              : destinationPortOptions.length === 0
                              ? "No ports/airports available"
                              : form.shippingMethod === "Air freight"
                              ? "Select Destination Airport"
                              : "Select Destination Port"
                          }
                          options={destinationPortOptions}
                          value={findOption(destinationPortOptions, form.destinationPort)}
                          onChange={(opt) => {
                            onChange({ target: { name: "destinationPort", value: opt?.value || "" } });
                          }}
                          isDisabled={!isDestinationPortEnabled()}
                          isClearable
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div className="rfq-field">
                  <div className="rfq-leadtime-group">
                    <span className="rfq-leadtime-text">Ship in</span>
                    <input
                      type="number"
                      name="leadTimeDays"
                      value={form.leadTimeDays}
                      onChange={onChange}
                      className="input-pill input-pill-small"
                      placeholder="0"
                    />
                    <span className="rfq-leadtime-text">
                      Day(s) after supplier receives the Initial Payment
                    </span>
                  </div>
                </div>

              </div>
            </div>

            {/* Logistics assistance */}
            <div className="rfq-main-content">
              <div className="rfq-section">
                <h6 className="rfq-section-heading">Need logistics assistance?</h6>
                <div className="rfq-field">
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      id="buyerLogisticsYes"
                      name="buyerLogisticsRequested"
                      checked={form.buyerLogisticsRequested === true}
                      onChange={() => setForm((prev) => ({ ...prev, buyerLogisticsRequested: true }))}
                    />
                    <label className="form-check-label" htmlFor="buyerLogisticsYes">Yes</label>
                  </div>
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      id="buyerLogisticsNo"
                      name="buyerLogisticsRequested"
                      checked={form.buyerLogisticsRequested !== true}
                      onChange={() => setForm((prev) => ({ ...prev, buyerLogisticsRequested: false }))}
                    />
                    <label className="form-check-label" htmlFor="buyerLogisticsNo">No</label>
                  </div>
                  <p className="text-muted small mt-2 mb-0">
                    This preference is saved with your RFQ. A logistics RFQ is created only after you accept a seller quotation, if required.
                  </p>
                </div>
              </div>
            </div>

            {/* Special Requirements & Notes Section */}
            <div className="rfq-main-content">
              <div className="rfq-section">
                <h6 className="rfq-section-heading">Special Requirements & Notes</h6>
                
                <div className="rfq-field">
                  <div className="row">
                    <div className="col-12 col-md-12 col-lg-12">
                      <textarea
                        name="specialRequirements"
                        value={form.specialRequirements}
                        onChange={onChange}
                        className="input-area"
                        rows="3"
                        placeholder="Please mention special requirements and notes that the seller should be aware of"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </main>

        {/* Completeness Sidebar */}
        <aside className="col-12 col-lg-4">
          <div className="rfq-sidebar">
            <div className="rfq-completeness-card">
              <h6 className="rfq-completeness-title">Completeness</h6>
              <div className={`rfq-status-indicator ${isComplete ? 'complete' : 'incomplete'}`}>
                {isComplete ? 'Complete' : 'Incomplete'}
              </div>
              <p className="rfq-completeness-text">
                The more precise information you write, the better response you will get.
              </p>
              {incompleteFields.length > 0 && (
                <div className="rfq-incomplete-fields">
                  <p className="rfq-incomplete-title">Please fill in the required fields before submitting.</p>
                  <ul className="rfq-incomplete-list">
                    {incompleteFields.map((field, idx) => (
                      <li key={idx}>{field}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </aside>
      </div>
      )}
    </div>
  );
}
